import { useCallback } from 'react';
import { toast } from 'sonner';

import { notificationApi } from '../services/notificationApi';
import type { Notification } from '../types/notification.types';
import {
  showNotificationToast,
  showBrowserNotification,
  isPageActive,
} from '../utils/notificationUtils';

export interface UseNotificationHandlersProps {
  notificationPermission: NotificationPermission | null;
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  setTotalNotifications: React.Dispatch<React.SetStateAction<number>>;
  setConnectionAttempts: React.Dispatch<React.SetStateAction<number>>;
}

export const useNotificationHandlers = ({
  notificationPermission,
  setNotifications,
  setUnreadCount,
  setTotalNotifications,
  setConnectionAttempts,
}: UseNotificationHandlersProps) => {
  const handleNewNotification = useCallback(
    (notification: Notification) => {
      setNotifications((prev) => [notification, ...prev]);
      setUnreadCount((prev) => prev + 1);
      setTotalNotifications((prev) => prev + 1);

      const markAsReadHandler = async (id: string) => {
        try {
          await notificationApi.markAsRead(id);
          setNotifications((prev) =>
            prev.map((n) =>
              n._id === id
                ? { ...n, is_read: true, read_at: new Date().toISOString() }
                : n,
            ),
          );
          setUnreadCount((prev) => Math.max(0, prev - 1));
          toast.dismiss(id);
        } catch (err) {
          console.error('Mark as read failed', err);
        }
      };

      // For danger/error notifications and dialog notifications, we don't show a toast as they will be handled by their respective providers
      const isDangerNotification = notification.type.toLowerCase() === 'error';
      const isDialogNotification = notification.type.toLowerCase() === 'dialog';

      if (!isDangerNotification && !isDialogNotification) {
        if (isPageActive()) {
          showNotificationToast(notification, markAsReadHandler);
        } else if (notificationPermission === 'granted') {
          showBrowserNotification(notification, markAsReadHandler);
        }
      }
    },
    [
      notificationPermission,
      setNotifications,
      setUnreadCount,
      setTotalNotifications,
    ],
  );

  const handleConnect = useCallback(() => {
    setConnectionAttempts(0);
  }, [setConnectionAttempts]);

  const handleDisconnect = useCallback(() => {
    // Handle disconnect logic if needed
  }, []);

  const handleError = useCallback(() => {
    setConnectionAttempts((prev) => prev + 1);
  }, [setConnectionAttempts]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await notificationApi.markAsRead(notificationId);
        setNotifications((prev) =>
          prev.map((n) =>
            n._id === notificationId
              ? { ...n, is_read: true, read_at: new Date().toISOString() }
              : n,
          ),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
        toast.dismiss(notificationId);
      } catch (err) {
        console.error('Mark as read failed', err);
      }
    },
    [setNotifications, setUnreadCount],
  );

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          is_read: true,
          read_at: new Date().toISOString(),
        })),
      );
      setUnreadCount(0);
      toast.dismiss();
    } catch (err) {
      console.error('Mark all as read failed', err);
    }
  }, [setNotifications, setUnreadCount]);

  return {
    handleNewNotification,
    handleConnect,
    handleDisconnect,
    handleError,
    markAsRead,
    markAllAsRead,
  };
};
